import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { QuestionCard } from "@/components/audit/QuestionCard";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Input = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/65">{label}</span>
    <input {...p} data-testid={testid} className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-xl outline-none focus:border-[#F59E0B] transition-colors placeholder:text-[#F5F1E8]/30" />
  </label>
);

export default function AuditPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const moduleId = params.get("modulo") || "express";
  const [mod, setMod] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [step, setStep] = useState(0);
  const [info, setInfo] = useState({ company: user?.company || "", site: "", auditor: user?.name || "" });
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get(`/modules/${moduleId}`).then((r) => { setMod(r.data); setBlocks(Array.isArray(r.data?.blocks) ? r.data.blocks : []); }).catch((e) => { if (e?.response?.status === 403) setLocked(true); else toast.error("No se pudo cargar el cuestionario"); });
  }, [moduleId]);

  if (locked) {
    return (
      <main className="min-h-screen" data-testid="audit-locked-page">
        <Navbar variant="app" />
        <div className="mx-auto max-w-xl px-5 pt-40 pb-24 text-center">
          <h1 className="font-display text-3xl font-extrabold">Módulo prémium</h1>
          <p className="mt-4 text-[#F5F1E8]/75">Este módulo requiere una suscripción activa a la plataforma o la contratación de la Póliza de Consultoría Ascentra.</p>
          <button onClick={() => navigate("/panel")} data-testid="audit-locked-back-button" className="mt-8 px-6 py-3 rounded-full bg-[#F59E0B] text-[#0B0F17] font-semibold">Volver al catálogo</button>
        </div>
      </main>
    );
  }

  const total = blocks.reduce((n, b) => n + b.questions.length, 0);
  const answered = Object.keys(answers).length;
  const block = blocks[step - 1];
  const offset = blocks.slice(0, Math.max(step - 1, 0)).reduce((n, b) => n + b.questions.length, 0);
  const missingPhoto = block ? block.questions.filter((q) => answers[q.id]?.answer === "no" && q.kind !== "documental" && !answers[q.id]?.photo_path) : [];
  const blockDone = block ? block.questions.every((q) => answers[q.id]) && missingPhoto.length === 0 : info.company && info.site && info.auditor;
  const isLast = step === blocks.length;

  const go = (s) => { setStep(s); window.__lenis?.scrollTo(0, { duration: 0.8 }); };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...info, module_id: moduleId, answers: Object.entries(answers).map(([question_id, v]) => ({ question_id, ...v })) };
      const { data } = await api.post("/audits", payload);
      toast.success("Auditoría guardada");
      navigate(`/resumen/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="audit-page">
      <Navbar variant="app" />
      <div className="fixed top-16 inset-x-0 z-40 h-1 bg-white/5">
        <motion.div className="h-full bg-[#F59E0B]" animate={{ width: `${total ? (answered / total) * 100 : 0}%` }} transition={{ duration: 0.5 }} data-testid="audit-progress-bar" />
      </div>
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 pt-32 pb-24 grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4 min-w-0">
          <div className="lg:sticky lg:top-28">
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]" data-testid="audit-progress-text">{answered} / {total || "—"} respondidas</div>
            {mod && <div className="mt-3 font-display text-lg font-bold leading-tight" data-testid="audit-module-name">{mod.name}<span className="block text-sm font-medium text-[#F5F1E8]/65">{mod.subtitle}</span></div>}
            <ol className="mt-6 flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0" data-testid="audit-steps">
              {[{ id: "info", code: "00", title: "Datos del centro" }, ...blocks].map((b, i) => {
                const done = i === 0 ? step > 0 : b.questions.every((q) => answers[q.id]);
                return (
                  <li key={b.id} className={`shrink-0 flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${i === step ? "border-[#F59E0B] bg-white/[0.04]" : "border-white/10"}`} data-testid={`audit-step-${i}`}>
                    <span className={`grid place-items-center w-6 h-6 rounded-full text-[11px] font-mono ${done ? "bg-[#F59E0B] text-[#0B0F17]" : "border border-white/20 text-[#F5F1E8]/60"}`}>{done ? <Check className="w-3 h-3" /> : i}</span>
                    <span className="text-sm whitespace-nowrap lg:whitespace-normal">{b.title}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        </aside>

        <section className="lg:col-span-8 min-w-0">
          <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
            {step === 0 ? (
              <div className="space-y-8" data-testid="audit-info-step">
                <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">{mod?.pillar === "iso" ? "Calidad y Certificación" : "Cumplimiento Legal STPS"}</div>
                <h1 className="font-display-x text-4xl sm:text-6xl font-black uppercase leading-[0.92] tracking-tight">{mod?.name ?? "Nueva"}<br /><span className="text-[#F59E0B] text-3xl sm:text-5xl">{mod?.subtitle ?? "auditoría"}</span></h1>
                <p className="text-[#F5F1E8]/70 max-w-lg">Identifica el centro de trabajo. Después responderás {total} reactivos en {blocks.length} bloques. Cada “No” habilita la foto de evidencia y se suma a tu plan de acción.</p>
                <Input label="Razón social / Empresa" testid="audit-company-input" value={info.company} onChange={(e) => setInfo({ ...info, company: e.target.value })} placeholder="Manufacturas del Norte S.A. de C.V." />
                <Input label="Centro de trabajo / Planta" testid="audit-site-input" value={info.site} onChange={(e) => setInfo({ ...info, site: e.target.value })} placeholder="Planta Apodaca, N.L." />
                <Input label="Auditor responsable" testid="audit-auditor-input" value={info.auditor} onChange={(e) => setInfo({ ...info, auditor: e.target.value })} />
              </div>
            ) : block && (
              <div data-testid={`audit-block-${block.id}`}>
                <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Bloque {step} de {blocks.length} · {block.code}</div>
                <h2 className="font-display mt-4 text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.02]">{block.title}</h2>
                <div className="mt-6 border-t border-white/10">
                  {block.questions.map((q, i) => (
                    <QuestionCard key={q.id} q={q} index={offset + i + 1} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {missingPhoto.length > 0 && (
            <p className="mt-8 text-sm text-rose-300" data-testid="audit-missing-photo-hint">Falta la foto de evidencia en {missingPhoto.length === 1 ? "1 hallazgo físico" : `${missingPhoto.length} hallazgos físicos`} para continuar.</p>
          )}
          <div className="mt-12 flex items-center justify-between gap-4">
            <button type="button" onClick={() => go(step - 1)} disabled={step === 0} data-testid="audit-prev-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-sm hover:border-white/40 transition-colors disabled:opacity-30">
              <ArrowLeft className="w-4 h-4" /> Anterior
            </button>
            {isLast && step > 0 ? (
              <button type="button" onClick={submit} disabled={!blockDone || answered < total || saving} data-testid="audit-submit-button" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#F59E0B] text-[#0B0F17] font-semibold hover:bg-[#F5F1E8] transition-colors disabled:opacity-40">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Finalizar y calcular
              </button>
            ) : (
              <button type="button" onClick={() => go(step + 1)} disabled={!blockDone || !blocks.length} data-testid="audit-next-button" className="group inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#F59E0B] text-[#0B0F17] font-semibold hover:bg-[#F5F1E8] transition-colors disabled:opacity-40">
                Siguiente <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
